/**
 * 法务文本与同意记录（POLICIES & CONSENTS）。
 *
 * 这是「版本管理 + 同意留痕 + 变更再同意」机制的成员侧入口，因此三件事必须做实、说准：
 *   1. 草案不可作为同意对象 —— status='draft' 或 acceptable=false 的文本，确认按钮一律禁用，
 *      并写明原因（与后端 core/policies.ts 的口径一致：对草案作出的「同意」只会产生
 *      一条无法解释、也无法举证的合规记录）；
 *   2. 待填内容必须看得见 —— 正文里的【…】与 [待确认 / 需法务…] 逐处高亮，
 *      工程占位草案（isPlaceholder）在阅读视图内显著标注「待法务确认，不得作为生效文本」；
 *   3. 待同意提示只由接口的 pending / needsAcceptance 驱动 —— 草案不触发强制同意，
 *      所以演示状态下本页不会出现待确认提示，这是正确状态，不为了让页面好看而伪造。
 *
 * 正文是 Markdown 纯文本，这里用局部函数 parseBlocks / renderInline 转成 React 元素渲染：
 * 不引入 Markdown 依赖，也不使用 dangerouslySetInnerHTML（后端文本由 React 自动转义）。
 */
import { Fragment, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ApiError, api } from '../../lib/api.ts';
import { formatDateTime } from '../../lib/format.ts';
import { useUi } from '../../store/ui.ts';
import { AlertStrip, Badge, Button, Card, ListItem, StateBox } from '../../components/ui.tsx';

/* ------------------------------------------------------------------ *
 * 接口返回结构（见 apps/api/src/core/policies.ts）
 * ------------------------------------------------------------------ */

type PolicyStatus = 'draft' | 'effective' | 'archived';

interface PolicyItem {
  kind: string;
  title: string;
  version: string;
  status: PolicyStatus;
  /** 正文仍是工程占位草案，待法务出具正式版本 */
  isPlaceholder: boolean;
  effectiveAt: string | null;
  /** 是否可作为同意对象：仅「已生效」的版本可以 */
  acceptable: boolean;
  accepted: boolean;
  acceptedAt: string | null;
  order: number;
}

interface PolicySummary {
  items: PolicyItem[];
  /** 需要（重新）取得同意的类别 */
  pending: string[];
  needsAcceptance: boolean;
}

interface PolicyDetail {
  document: {
    kind: string;
    title: string;
    version: string;
    status: PolicyStatus;
    isPlaceholder: boolean;
    effectiveAt: string | null;
    body: string;
  };
  accepted: boolean;
  acceptedAt: string | null;
  acceptable: boolean;
}

interface PolicyAcceptance {
  kind: string;
  title: string;
  version: string;
  acceptedAt: string;
  ip: string;
  userAgent: string;
}

interface AcceptanceList {
  items: PolicyAcceptance[];
}

/* ------------------------------------------------------------------ *
 * 状态徽标：三种情形严格对应接口语义，不合并、不美化
 * ------------------------------------------------------------------ */

interface BadgeState {
  tone: 'ok' | 'warn' | 'ghost';
  text: string;
}

function statusBadge(flags: { status: PolicyStatus; isPlaceholder: boolean; accepted: boolean }): BadgeState {
  if (flags.status === 'draft' || flags.isPlaceholder) return { tone: 'warn', text: '待法务确认' };
  if (flags.status === 'effective' && flags.accepted) return { tone: 'ok', text: '已确认' };
  if (flags.status === 'effective') return { tone: 'warn', text: '待确认' };
  // 归档版本（currentDocument 正常不会返回）：既非生效文本，也不可确认
  return { tone: 'ghost', text: '历史版本' };
}

/* ------------------------------------------------------------------ *
 * 极简 Markdown 渲染（局部实现，不引入依赖）
 * ------------------------------------------------------------------ */

type BlockType = 'h1' | 'h2' | 'h3' | 'p' | 'ul' | 'ol' | 'quote' | 'hr';

interface TextBlock {
  type: BlockType;
  lines: string[];
}

/** 草稿在句中断行是排版行为，合段时中文之间不补空格，西文之间补一个空格 */
const CJK_TAIL = /[\u3000-\u303f\u4e00-\u9fa5\uff00-\uffef]$/;

function joinParagraph(lines: string[]): string {
  return lines.reduce((acc, line) => {
    if (acc === '') return line;
    return CJK_TAIL.test(acc) ? `${acc}${line}` : `${acc} ${line}`;
  }, '');
}

function parseBlocks(body: string): TextBlock[] {
  const blocks: TextBlock[] = [];
  const lines = body.replace(/\r\n/g, '\n').split('\n');
  let paragraph: string[] = [];
  let listType: 'ul' | 'ol' | null = null;
  let listItems: string[] = [];
  let quote: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      blocks.push({ type: 'p', lines: [joinParagraph(paragraph)] });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (listType !== null && listItems.length > 0) blocks.push({ type: listType, lines: listItems });
    listType = null;
    listItems = [];
  };
  const flushQuote = () => {
    if (quote.length > 0) {
      blocks.push({ type: 'quote', lines: quote });
      quote = [];
    }
  };
  const flushAll = () => {
    flushParagraph();
    flushList();
    flushQuote();
  };

  for (const raw of lines) {
    const trimmed = raw.trim();
    if (trimmed === '') {
      flushAll();
      continue;
    }
    if (/^-{3,}$/.test(trimmed)) {
      flushAll();
      blocks.push({ type: 'hr', lines: [] });
      continue;
    }
    const heading = /^(#{1,4})\s+(.*)$/.exec(trimmed);
    if (heading) {
      flushAll();
      const level = heading[1].length;
      blocks.push({ type: level === 1 ? 'h1' : level === 2 ? 'h2' : 'h3', lines: [heading[2]] });
      continue;
    }
    if (trimmed.startsWith('>')) {
      flushParagraph();
      flushList();
      quote.push(trimmed.replace(/^>\s?/, ''));
      continue;
    }
    const bullet = /^[-*·]\s+(.*)$/.exec(trimmed);
    if (bullet) {
      flushParagraph();
      flushQuote();
      if (listType !== 'ul') flushList();
      listType = 'ul';
      listItems.push(bullet[1]);
      continue;
    }
    const ordered = /^\d+[.、)]\s+(.*)$/.exec(trimmed);
    if (ordered) {
      flushParagraph();
      flushQuote();
      if (listType !== 'ol') flushList();
      listType = 'ol';
      listItems.push(ordered[1]);
      continue;
    }
    flushList();
    flushQuote();
    paragraph.push(trimmed);
  }
  flushAll();
  return blocks;
}

/** 占位内容高亮样式：正文里最需要被看见的就是「哪里还没填」 */
const placeholderStyle = {
  fontSize: 11.5,
  lineHeight: 1.7,
  padding: '1px 7px',
  whiteSpace: 'normal',
  verticalAlign: 'baseline',
} as const;

/**
 * 行内渲染：**加粗**、`代码`、【占位符】、[待确认 / 需法务…] 高亮。
 * 全部产出 React 元素（字符串由 React 转义），不存在 HTML 注入面。
 */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern = /(\*\*[^*\n]+\*\*|`[^`\n]+`|【[^】\n]*】|\[[^\]\n]*\])/g;
  let cursor = 0;
  let index = 0;
  let match = pattern.exec(text);
  while (match !== null) {
    if (match.index > cursor) nodes.push(text.slice(cursor, match.index));
    const token = match[0];
    const key = `${keyPrefix}-${index}`;
    index += 1;
    if (token.startsWith('**')) {
      // 加粗内部仍可能出现【占位符】，因此递归一次（内层不含 * ，不会无限递归）
      nodes.push(<strong key={key}>{renderInline(token.slice(2, -2), key)}</strong>);
    } else if (token.startsWith('`')) {
      nodes.push(
        <span key={key} className="mono-num" style={{ fontSize: 11.5, color: 'var(--e)' }}>
          {token.slice(1, -1)}
        </span>,
      );
    } else if (token.startsWith('[') && text.charAt(match.index + token.length) === '(') {
      // Markdown 链接语法：本页不引入渲染器，按原样文本呈现，避免误高亮
      nodes.push(token);
    } else {
      nodes.push(
        <span key={key} className="badge warn" style={placeholderStyle}>
          {token}
        </span>,
      );
    }
    cursor = match.index + token.length;
    match = pattern.exec(text);
  }
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes;
}

function PolicyBody({ body }: { body: string }) {
  const blocks = parseBlocks(body);
  return (
    <div style={{ fontSize: 12.5, lineHeight: 1.85, color: 'var(--text-2)' }}>
      {blocks.map((block, blockIndex) => {
        const key = `blk-${blockIndex}`;
        if (block.type === 'hr') return <div key={key} className="hr" />;
        if (block.type === 'h1') {
          return (
            <div key={key} style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--text)', margin: '6px 0 9px' }}>
              {renderInline(block.lines[0] ?? '', key)}
            </div>
          );
        }
        if (block.type === 'h2') {
          return (
            <div key={key} style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--h)', margin: '17px 0 7px' }}>
              {renderInline(block.lines[0] ?? '', key)}
            </div>
          );
        }
        if (block.type === 'h3') {
          return (
            <div key={key} style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: '14px 0 6px' }}>
              {renderInline(block.lines[0] ?? '', key)}
            </div>
          );
        }
        if (block.type === 'quote') {
          return (
            <div
              key={key}
              style={{
                margin: '0 0 12px',
                padding: '9px 11px',
                borderRadius: 10,
                background: 'rgba(255,176,32,.07)',
                borderLeft: '2px solid var(--warn)',
              }}
            >
              {block.lines.map((line, lineIndex) => (
                <div key={`${key}-${lineIndex}`} style={{ fontSize: 11.5, lineHeight: 1.8, color: 'var(--text-2)' }}>
                  {renderInline(line, `${key}-${lineIndex}`)}
                </div>
              ))}
            </div>
          );
        }
        if (block.type === 'ul' || block.type === 'ol') {
          const ListTag = block.type === 'ul' ? 'ul' : 'ol';
          return (
            <ListTag
              key={key}
              style={{
                margin: '0 0 11px',
                paddingLeft: 19,
                listStyleType: block.type === 'ul' ? 'disc' : 'decimal',
              }}
            >
              {block.lines.map((line, lineIndex) => (
                <li key={`${key}-${lineIndex}`} style={{ fontSize: 12.5, lineHeight: 1.8, marginBottom: 5 }}>
                  {renderInline(line, `${key}-${lineIndex}`)}
                </li>
              ))}
            </ListTag>
          );
        }
        return (
          <div key={key} style={{ margin: '0 0 11px' }}>
            {renderInline(block.lines[0] ?? '', key)}
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * 阅读视图：正文 + 确认入口（后者由后端返回的 acceptable 决定可用性）
 * ------------------------------------------------------------------ */

const placeholderNotice = { color: 'var(--warn)', borderLeftColor: 'var(--warn)', marginTop: 9 } as const;

interface PolicyReaderProps {
  kind: string;
  /** 确认动作交给页面统一处理：成功后可同时失效列表、同意记录并收起阅读视图 */
  onAccept: (kind: string) => void;
  accepting: boolean;
}

function PolicyReader({ kind, onAccept, accepting }: PolicyReaderProps) {
  const detailQuery = useQuery({
    queryKey: ['policies', kind],
    queryFn: () => api.get<PolicyDetail>(`/api/policies/${kind}`),
  });

  if (detailQuery.isLoading) return <StateBox kind="loading" title="正在取回正文…" />;

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <StateBox
        kind="error"
        title="正文加载失败"
        desc="未能取回该文本的正文，因此这里不展示任何内容、也不提供确认入口。"
        action={
          <Button small onClick={() => void detailQuery.refetch()}>
            重新加载
          </Button>
        }
      />
    );
  }

  const detail = detailQuery.data;
  const doc = detail.document;
  const canAccept = detail.acceptable && !detail.accepted;
  const badge = statusBadge({ status: doc.status, isPlaceholder: doc.isPlaceholder, accepted: detail.accepted });

  return (
    <>
      <div className="row between" style={{ alignItems: 'flex-start', gap: 10 }}>
        <span style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.5 }}>{doc.title}</span>
        <Badge tone={badge.tone}>{badge.text}</Badge>
      </div>
      <div className="text-tiny text-muted" style={{ marginTop: 5, lineHeight: 1.7 }}>
        版本 {doc.version} ·{' '}
        {doc.effectiveAt ? `生效时间 ${formatDateTime(doc.effectiveAt)}` : '尚未生效（无生效时间）'}
        {detail.accepted && detail.acceptedAt
          ? ` · 您已于 ${formatDateTime(detail.acceptedAt)} 确认本版本`
          : ''}
      </div>

      {(doc.isPlaceholder || doc.status === 'draft') && (
        <div className="note-src" style={placeholderNotice}>
          <b style={{ color: 'var(--warn)' }}>
            {doc.isPlaceholder ? '待法务确认，不得作为生效文本' : '本篇尚未生效'}
          </b>
          {doc.isPlaceholder
            ? '：本篇正文仍为工程占位草案，由产品与工程团队按现行法规结构起草，尚未经法务定稿；'
            : '：本篇当前为草案版本，尚未由法务定稿发布；'}
          {doc.status === 'draft' ? `版本 ${doc.version} 处于 draft 状态，不具备作为同意对象的效力；` : ''}
          正文中以高亮标出的 【…】 与 [待确认 / 需法务…] 均为待填内容，请以法务定稿后的生效版本为准。
        </div>
      )}

      <div className="divider" />

      <PolicyBody body={doc.body} />

      <div className="divider" />

      <Button
        block
        variant="primary"
        disabled={!canAccept || accepting}
        onClick={() => onAccept(kind)}
      >
        {accepting ? '正在记录确认…' : detail.accepted ? '本版本已确认' : '我已阅读并确认同意'}
      </Button>

      {!detail.acceptable && (
        <div className="sec-note" style={{ color: 'var(--warn)' }}>
          不提供确认按钮的原因：本篇（版本 {doc.version}）尚未由法务定稿生效，草案不具备作为同意对象
          的效力 —— 若允许对草案作出「同意」，只会产生一条无法解释、也无法举证的合规记录。
          法务定稿并以生效版本发布后，此处会出现确认按钮；在此之前您的任何点击都不会产生同意记录。
        </div>
      )}
      {detail.acceptable && !detail.accepted && (
        <div className="sec-note">
          确认后系统会记录您所确认的<strong>版本、时间、IP 与客户端</strong>并写入不可篡改审计链；
          文本发布新版本后旧版本归档，您需要对新版本重新确认（变更再同意）。
        </div>
      )}
      {detail.acceptable && detail.accepted && (
        <div className="sec-note">
          本版本确认留痕：
          {detail.acceptedAt ? formatDateTime(detail.acceptedAt) : '时间未返回'}，可在下方「我的同意记录」中逐条核对。
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ *
 * 页面
 * ------------------------------------------------------------------ */

export function PoliciesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showToast = useUi((s) => s.showToast);
  const [openKind, setOpenKind] = useState<string | null>(null);
  const readerRef = useRef<HTMLDivElement | null>(null);

  const policiesQuery = useQuery({
    queryKey: ['policies'],
    queryFn: () => api.get<PolicySummary>('/api/policies'),
  });

  const acceptancesQuery = useQuery({
    queryKey: ['policies', 'acceptances'],
    queryFn: () => api.get<AcceptanceList>('/api/policies/acceptances'),
  });

  const acceptPolicy = useMutation({
    mutationFn: (kind: string) => api.post<PolicySummary>(`/api/policies/${kind}/accept`),
    onSuccess: (data) => {
      // 接口已返回更新后的清单，先就地更新，再统一失效（含 ['policies', kind] 与同意记录）
      queryClient.setQueryData<PolicySummary>(['policies'], data);
      void queryClient.invalidateQueries({ queryKey: ['policies'] });
      showToast('已记录您的确认：版本、时间与 IP 已写入审计链');
      setOpenKind(null);
    },
    onError: (error) => {
      showToast(error instanceof ApiError ? error.message : '确认失败，请稍后重试', 'error');
    },
  });

  // 展开阅读视图时把它滚入视野（从顶部提示跳转过来时尤其需要）
  useEffect(() => {
    if (openKind === null) return;
    readerRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [openKind]);

  const header = (
    <div className="scr-head">
      <button type="button" className="back" onClick={() => navigate(-1)} aria-label="返回">
        <svg viewBox="0 0 24 24">
          <path d="M15 5l-7 7 7 7" />
        </svg>
      </button>
      <div className="scr-title">
        法务文本与同意记录<small>POLICIES &amp; CONSENTS</small>
      </div>
    </div>
  );

  if (policiesQuery.isLoading) {
    return (
      <div className="page">
        {header}
        <div className="skeleton skel-card" style={{ height: 96 }} />
        <div className="skeleton skel-card" style={{ height: 320 }} />
        <div className="skeleton skel-card" style={{ height: 140 }} />
      </div>
    );
  }

  if (policiesQuery.isError || !policiesQuery.data) {
    return (
      <div className="page">
        {header}
        <StateBox
          kind="error"
          title="法务文本列表加载失败"
          desc="未能取回文本清单与您的同意状态。加载失败时本页不展示任何默认同意状态，请重试。"
          action={
            <Button small onClick={() => void policiesQuery.refetch()}>
              重新加载
            </Button>
          }
        />
      </div>
    );
  }

  const summary = policiesQuery.data;
  const items = [...summary.items].sort((a, b) => a.order - b.order);
  const pendingCount = summary.pending.length;
  const titleOfKind = (kind: string) => items.find((item) => item.kind === kind)?.title ?? kind;

  const acceptances = acceptancesQuery.data?.items ?? [];
  const acceptedCount = items.filter((item) => item.accepted).length;
  const notEffectiveCount = items.filter((item) => item.status !== 'effective').length;

  return (
    <div className="page">
      {header}

      {/* ---------------- 本页作用与「草案不可同意」的原因 ---------------- */}
      <div className="note-src">
        <b>本页是什么</b>：平台与您之间的法务文本（隐私政策、用户服务协议、个人信息处理知情同意书等）
        集中在这里。您可以逐篇查阅正文，并对<strong>已由法务定稿生效的版本</strong>作出确认；
        每次确认都会记录版本、时间、IP 与客户端并写入不可篡改审计链，作为「您当时同意的究竟是哪一版」的举证依据。
        <br />
        <b>为什么草案不能确认</b>：status 为 draft、或标注「工程占位草案」的文本尚未由法务定稿生效，
        <strong>不具备作为同意对象的效力</strong> —— 对它作出「同意」只会产生一条无法解释的合规记录。
        因此这些文本的确认按钮处于禁用状态并写明原因；法务定稿并以生效版本发布后，此处会出现确认按钮。
        本页的确认与「隐私与授权」中的四级数据授权是两回事，二者互不替代。
      </div>

      {/* 待同意提示只由接口的 pending / needsAcceptance 驱动；草案不触发强制同意 */}
      {summary.needsAcceptance && (
        <div style={{ marginTop: 10 }}>
          <AlertStrip
            tone="warn"
            title={`有 ${pendingCount} 篇文本需要您确认`}
            sub={`涉及：${summary.pending.map(titleOfKind).join('、')}。点击查看第一份待确认文本。`}
            onClick={() => setOpenKind(summary.pending[0] ?? null)}
          />
        </div>
      )}

      {/* ---------------- 文本列表 ---------------- */}
      <Card
        title="法务文本"
        more={
          <span className="card-more">
            共 {items.length} 篇 · 已确认 {acceptedCount} 篇 · 点击任意一行查阅正文
          </span>
        }
      >
        {items.length === 0 ? (
          <StateBox
            title="暂无可查阅的法务文本"
            desc="文本类别由平台预置，若长时间为空请联系您的专属管家。"
          />
        ) : (
          items.map((item) => {
            const open = openKind === item.kind;
            const badge = statusBadge(item);
            return (
              <Fragment key={item.kind}>
                <ListItem
                  title={item.title}
                  sub={
                    <>
                      版本 {item.version} ·{' '}
                      {item.effectiveAt ? `生效于 ${formatDateTime(item.effectiveAt)}` : '尚未生效'}
                      {item.accepted && item.acceptedAt
                        ? ` · 您已于 ${formatDateTime(item.acceptedAt)} 确认`
                        : ''}
                    </>
                  }
                  right={
                    <span className="row" style={{ gap: 7 }}>
                      <Badge tone={badge.tone}>{badge.text}</Badge>
                      <span aria-hidden style={{ color: 'var(--muted-2)' }}>
                        {open ? '收起' : '阅读'}
                      </span>
                    </span>
                  }
                  onClick={() => setOpenKind(open ? null : item.kind)}
                />
                {open && (
                  <div
                    ref={readerRef}
                    style={{
                      margin: '2px 0 14px',
                      padding: '13px 13px 15px',
                      borderRadius: 12,
                      background: 'rgba(255,255,255,.032)',
                      border: '1px solid var(--line)',
                    }}
                  >
                    <PolicyReader
                      kind={item.kind}
                      onAccept={(kind) => acceptPolicy.mutate(kind)}
                      accepting={acceptPolicy.isPending && acceptPolicy.variables === item.kind}
                    />
                  </div>
                )}
              </Fragment>
            );
          })
        )}

        {items.length > 0 && (
          <div className="sec-note">
            当前清单中 {notEffectiveCount} 篇尚未生效（草案或占位稿），因此不能作为同意对象；
            其余 {items.length - notEffectiveCount} 篇以生效版本为准。
          </div>
        )}
      </Card>

      {/* ---------------- 我的同意记录 ---------------- */}
      <Card
        title="我的同意记录"
        more={
          <span className="card-more">
            {acceptancesQuery.data ? `共 ${acceptances.length} 条` : '加载中…'}
          </span>
        }
      >
        {acceptancesQuery.isLoading && <StateBox kind="loading" title="正在加载同意记录…" />}

        {acceptancesQuery.isError && (
          <StateBox
            kind="error"
            title="同意记录加载失败"
            desc="同意留痕是您日后举证的依据，加载失败时请重试而不是忽略。"
            action={
              <Button small onClick={() => void acceptancesQuery.refetch()}>
                重新加载
              </Button>
            }
          />
        )}

        {acceptancesQuery.data && acceptances.length === 0 && (
          <StateBox
            title="您尚未确认任何文本"
            desc="在法务定稿生效后，您的每次确认都会记录版本、时间与 IP 并写入审计链。"
          />
        )}

        {acceptances.map((item) => (
          <ListItem
            key={`${item.kind}-${item.version}-${item.acceptedAt}`}
            title={item.title}
            sub={
              <>
                确认时间 {formatDateTime(item.acceptedAt)}
                <br />
                IP {item.ip}
                {item.userAgent ? ` · 客户端 ${item.userAgent}` : ''}
              </>
            }
            right={<Badge tone="ok">{item.version}</Badge>}
          />
        ))}

        <div className="sec-note">
          记录只允许追加、不允许改写；每条确认同时写入不可篡改审计链，可在「数据调用记录」中按哈希逐条核对。
        </div>
      </Card>

      <div className="note-src">
        <b>口径说明</b>：本页只呈现平台与您之间的法务文本及其版本状态，不构成法律意见；文本由法务出具并定稿，
        平台不会在文本生效前要求您确认，也不会以「已完成授权」替代您的明示确认。
        平台不开展临床诊疗，医疗服务由具备资质的合作医疗机构执行；金融、信托、法务类业务由持牌合作机构落地。
      </div>

      <div className="sec-note text-center">
        版本管理 · 同意留痕 · 变更再同意 · 哈希链审计
      </div>
    </div>
  );
}
